import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import podImage from "../assets/pod-design.png";

// Animated counter component - uses linear interpolation for snappy finish
const AnimatedCounter = ({ target, delay = 1 }: { target: number; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [count, setCount] = useState(1500);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!isVisible) return;
    
    const startValue = 1500;
    const duration = 500; // 0.5 seconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic for smooth but fast finish
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startValue + (target - startValue) * eased);
      setCount(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, target]);

  return (
    <motion.p 
      className="font-funnel text-7xl font-bold text-foreground"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.9 }}
      transition={{ duration: 0.2 }}
    >
      £{count.toLocaleString()}
    </motion.p>
  );
};

// Version 5: Original layout with logo at top and CTA button
const PartnerAd5 = () => {
  return (
    <div className="h-[100dvh] bg-background flex flex-col items-center justify-between px-6 py-6 overflow-hidden relative">
      {/* Subtle background glow */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          background: "radial-gradient(circle at 50% 40%, hsl(var(--foreground)) 0%, transparent 60%)"
        }}
      />

      {/* Top: Logo - fades in at the end */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 2.2 }}
      >
        <span className="font-funnel text-2xl font-bold text-foreground">qwip</span>
      </motion.div>

      {/* Center content */}
      <div className="flex flex-col items-center flex-1 justify-center">
        {/* The hook - identify the audience */}
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          className="text-foreground text-center text-3xl font-medium leading-snug mb-3"
        >
          Got 1m² of spare space
          <br />
          in London?
        </motion.p>

        {/* Pod image - slides in */}
        <motion.img
          src={podImage}
          alt="qwip soundproof pod"
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.5, type: "spring", stiffness: 100 }}
          className="w-32 drop-shadow-xl mb-3"
        />

        {/* Value proposition */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 0.8 }}
          className="text-muted-foreground text-lg mb-1"
        >
          Host a qwip pod, and earn up to
        </motion.p>

        {/* THE MONEY - animated counter */}
        <div className="text-center mb-1">
          <AnimatedCounter target={3000} delay={1} />
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 1.2 }}
          className="text-muted-foreground text-lg mb-4"
        >
          per month
        </motion.p>

        {/* Trust builder - single bubble */}
        <motion.span
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 1.35 }}
          className="bg-foreground/10 text-foreground text-base font-medium px-5 py-2.5 rounded-full"
        >
          Zero upfront cost
        </motion.span>
      </div>

      {/* Bottom: CTA - fades in at the end */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 2.2 }}
        className="text-center w-full"
      >
        <a
          href="/partner"
          className="inline-block w-full max-w-xs bg-foreground text-primary-foreground px-5 py-3 rounded-full font-funnel font-semibold text-base shadow-xl"
        >
          Contact us to learn more
        </a>
        <p className="text-muted-foreground text-xs mt-2">
          qwip.co.uk/partner
        </p>
      </motion.div>
    </div>
  );
};

export default PartnerAd5;
